import React from "react";
import { Check, Filter, Minus, Plus } from "lucide-react";

type FindingsSummaryProps = {
  canUpdateActiveSeries: boolean;
  currentCount: number;
  isUpdatingReport: boolean;
  reportEditCount: number;
  selectedCount: number;
  showReportMode: boolean;
  totalCount: number;
  t: (key: string, options?: Record<string, unknown>) => string;
  onSubmitReport: () => void;
  onToggleReportMode: () => void;
};

export const FindingsSummary = ({
  canUpdateActiveSeries,
  currentCount,
  isUpdatingReport,
  reportEditCount,
  selectedCount,
  showReportMode,
  totalCount,
  t,
  onSubmitReport,
  onToggleReportMode,
}: FindingsSummaryProps) => (
  <div className="flex-shrink-0 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-4 shadow-sm mb-6 mx-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-sm font-medium text-slate-700 dark:text-gray-300">
            {t("summary.countOf", { filtered: currentCount, total: totalCount })}
          </span>
        </div>
        {showReportMode && selectedCount > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-sm font-medium text-green-700 dark:text-green-400">
              {t("summary.selected", { count: selectedCount })}
            </span>
          </div>
        )}
        {showReportMode && reportEditCount > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
              {t("summary.changes", { count: reportEditCount })}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {canUpdateActiveSeries && (
          <button
            onClick={onToggleReportMode}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${showReportMode
              ? "bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-600"
              : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
              }`}
          >
            {showReportMode ? (
              <>
                <Minus size={14} />
                {t("reportMode.cancel")}
              </>
            ) : (
              <>
                <Plus size={14} />
                {t("reportMode.addToReport")}
              </>
            )}
          </button>
        )}

        {showReportMode && canUpdateActiveSeries && (
          <button
            onClick={onSubmitReport}
            disabled={isUpdatingReport || (selectedCount === 0 && reportEditCount === 0)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${(selectedCount > 0 || reportEditCount > 0) && !isUpdatingReport
              ? "bg-green-600 text-white hover:bg-green-700 shadow-sm"
              : "bg-slate-200 dark:bg-gray-600 text-slate-400 dark:text-gray-500 cursor-not-allowed"
              }`}
          >
            <Check size={14} />
            {isUpdatingReport ? t("reportMode.saving") : t("reportMode.add", { count: selectedCount })}
          </button>
        )}
      </div>
    </div>

    {showReportMode && canUpdateActiveSeries && (
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg mt-4">
        <div className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
          <Filter size={16} />
          <div>
            <span className="text-sm font-medium">{t("reportMode.selectionModeActive")}</span>
            <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">{t("reportMode.selectionModeHint")}</p>
          </div>
        </div>
      </div>
    )}
  </div>
);
